interface BillingSubscription {
  status: string;
  stripe_subscription_id: string;
  current_period_end?: string | null;
}

export type SubscriptionManagementResponse = {
  code: 'subscription_management_required';
  action: 'billing_portal';
  reason: 'payment_recovery' | 'existing_subscription';
  status: string;
};

/** Only terminal subscriptions permit another subscription Checkout. */
export function subscriptionManagementReason(status: string): SubscriptionManagementResponse['reason'] | null {
  if (status === 'canceled' || status === 'incomplete_expired') return null;
  if (status === 'past_due' || status === 'unpaid') return 'payment_recovery';
  if (['active', 'trialing', 'paused', 'incomplete'].includes(status)) return 'existing_subscription';
  // New/unknown Stripe statuses must not silently enable a duplicate.
  throw new Error('Unknown subscription status');
}

export function isSubscriptionManagementResponse(value: unknown): value is SubscriptionManagementResponse {
  if (!value || typeof value !== 'object') return false;
  const result = value as Partial<SubscriptionManagementResponse>;
  if (result.code !== 'subscription_management_required' || result.action !== 'billing_portal' || typeof result.status !== 'string') return false;
  try {
    const reason = subscriptionManagementReason(result.status);
    return reason !== null && reason === result.reason;
  } catch {
    return false;
  }
}

/** An existing subscription needs its invoice resolved, not another checkout. */
export function needsBillingRecovery(subscription: BillingSubscription | null): boolean {
  return !!subscription
    && subscription.stripe_subscription_id.startsWith('sub_')
    && ['past_due', 'unpaid'].includes(subscription.status);
}

/** Only ended subscriptions can receive the editor's restart offer. */
export function isWinbackEligible(subscription: BillingSubscription | null, now = Date.now()): boolean {
  if (!subscription || subscription.status !== 'canceled' || !subscription.current_period_end) return false;
  const end = Date.parse(subscription.current_period_end);
  return Number.isFinite(end) && end < now && now - end < 14 * 86_400_000;
}
